import { FC, lazy, Suspense, useEffect, useMemo } from "react";
import { Stack } from "@fluentui/react";
import { FixedList } from "cayo.ui";
import { normalizePropertyNames } from "@cayo/admin-components/components/grids/utils";
import { apiMocks } from "@cayo/core/admin-components/grids/SpnGrid/apiMocks";
import { SelectServicesDialog } from "@cayo/core/admin-components/grids/SpnGrid/components/SelectServicesDialog";
import { ISpnGrid, ISpnGridItem } from "@cayo/core/admin-components/grids/SpnGrid/types";
import { useSpnGrid } from "@cayo/core/admin-components/grids/SpnGrid/useSpnGrid";
import { groupParsedSpns } from "@cayo/core/admin-components/grids/SpnGrid/utils";
import { withHidden } from "@cayo/shared/components/Hoc/withHidden";

const ObjectSelectionDialog = lazy(
  () => import("@cayo/core/admin-components/dialogs/ObjectSelectionDialog/ObjectSelectionDialog")
);

interface SpnGridProps extends ISpnGrid {
  // TODO: should be ISpnGridItem[] instead of string[] after GraphAPI fix
  items?: { collapsed: string[]; expanded: string[] };
}

const listItemKey = "originalSPN"; // TODO: get from backend

const SpnGrid: FC<SpnGridProps> = ({
  name,
  readOnly,
  label,
  items = { expanded: [], collapsed: [] },
  disabled,
  columns,
  dataTestId,
  selectionMode,
  layoutMode,
  setValue,
}) => {
  // set initial items once on mount, deps array must be empty
  const initialExpanded: ISpnGridItem[] = useMemo(
    () => normalizePropertyNames(items.expanded.map((item) => JSON.parse(item))),
    []
  );

  const {
    commands,
    isPickerVisible,
    hidePicker,
    cayoObjId,
    setCayoObjId,
    clearCayoObjId,
    addServices,
    items: expandedItems,
    isExpanded,
  } = useSpnGrid(initialExpanded, readOnly);

  const displayedItems = useMemo(
    () => (isExpanded ? expandedItems : groupParsedSpns(expandedItems)),
    [expandedItems, isExpanded]
  );

  useEffect(() => {
    if (expandedItems === initialExpanded) {
      return;
    }
    setValue(expandedItems);
  }, [expandedItems]);

  return (
    <Stack>
      {isPickerVisible && (
        <Suspense fallback={null}>
          <ObjectSelectionDialog
            searchScopes={apiMocks.searchScopes as any}
            modalOptions={apiMocks.modalOptions}
            keyProp={listItemKey}
            onClose={() => {
              hidePicker();
            }}
            onSelected={(items) => {
              setCayoObjId(items[0]?.cayoObjId);
              return Promise.resolve();
            }}
          />
        </Suspense>
      )}
      {cayoObjId && (
        <SelectServicesDialog
          cayoObjId={cayoObjId}
          isDialogVisible={Boolean(cayoObjId)}
          dismissDialog={clearCayoObjId}
          onConfirm={addServices}
        />
      )}
      <FixedList
        name={name!}
        label={label}
        value={displayedItems}
        columns={columns}
        readOnly={readOnly}
        disabled={disabled}
        dataTestId={dataTestId}
        setValue={() => {}}
        layoutMode={layoutMode ?? "fixedColumns"}
        selectionMode={selectionMode ?? "multiple"}
        commands={commands}
        listItemKey={listItemKey}
      />
    </Stack>
  );
};

export default withHidden(SpnGrid);
